import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Check, X, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import OpenOpnieuw from "./OpenOpnieuw";
import { useTaal } from "@/contexts/TaalContext";

/** Twee zelf-geleerde geheugenregels die dubbel of verouderd lijken. A2.
 *
 *  Besluit Frank 11 oktober 2026: er verdwijnt nooit stil een regel. Deze kaart
 *  zegt wat er zou gebeuren; pas na "Ruim op" gaat de ene regel weg, via het
 *  WF-40-token zoals bij het skillvoorstel. "Laat staan" houdt beide, en dit paar
 *  komt niet terug.
 */
export default function GeheugenVoorstel({ voorstel, onBesloten }) {
  const { t } = useTaal();
  const [rij, setRij] = useState(voorstel);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const { token, vernieuw, vervallen, laatVervallen, reden } = useKaartToken(
    `${API}/postbus/geheugen/${voorstel.id}/toon`, rij.status === "open");
  const samen = rij.actie === "samenvoegen";

  const beslis = async (actie) => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/postbus/geheugen/${rij.id}/${actie}`, { token });
      setRij(r.data);
      onBesloten?.(r.data);
    } catch (err) {
      const tekst = err?.response?.data?.detail || t("Er ging iets mis; er is niets veranderd.");
      setFout(tekst);
      laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  if (rij.status !== "open") {
    const zin = rij.status === "aangenomen"
      ? (samen ? t("Samengevoegd. Er staat nu een regel.") : t("Vervangen. De oudere regel is weg."))
      : rij.status === "vervallen"
        ? t("Een van de twee regels bestond niet meer; er is niets veranderd.")
        : t("Laten staan. Beide regels blijven, en dit paar komt niet terug.");
    return (
      <div className="border rounded-lg p-3" data-testid="geheugen-besloten">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">{zin}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 space-y-2" data-testid="geheugen-voorstel">
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {samen
          ? t("Deze twee geleerde regels zeggen vrijwel hetzelfde. Samenvoegen houdt de bovenste.")
          : t("De bovenste, nieuwere regel lijkt de onderste te hebben ingehaald. Vervangen haalt de onderste weg.")}
        {" "}{t("Er gebeurt niets tot je kiest.")}
      </p>
      <p className="text-sm break-words" data-testid="geheugen-houd">
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{t("Blijft:")} </span>{rij.houd ?? t("(niet meer leesbaar)")}
      </p>
      <p className="text-sm break-words" data-testid="geheugen-weg">
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{t("Gaat weg:")} </span>
        <span className="line-through decoration-1 opacity-70">{rij.weg ?? t("(niet meer leesbaar)")}</span>
      </p>
      {(fout || reden) && <p className="text-xs text-[hsl(var(--destructive))]" data-testid="geheugen-fout">{fout || reden}</p>}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className={`min-h-11 px-4 ${vervallen ? GRIJS : ""}`} disabled={bezig || !token}
                onClick={() => beslis("aannemen")} data-testid="geheugen-aannemen">
          {bezig ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
          {samen ? t("Voeg samen") : t("Vervang")}
        </Button>
        <Button size="sm" variant="outline" className="min-h-11 px-4" disabled={bezig || !token}
                onClick={() => beslis("weigeren")} data-testid="geheugen-weigeren">
          <X className="h-3 w-3 mr-1" /> {t("Laat staan")}
        </Button>
        {vervallen && <OpenOpnieuw onClick={vernieuw} bezig={bezig} />}
      </div>
    </div>
  );
}
